/**
 * `<UiScaleSettings>` — the global UI scale slider.
 *
 * Scales the WHOLE app via CSS `zoom` on the document root (`applyUiScale`), so every size — px
 * and rem alike, across all surfaces, including modals/toasts that portal to <body> — scales
 * uniformly. No backend setting: a per-device preference persisted in `localStorage` (key
 * `aletheia.ui_scale`) and applied at boot from `main.tsx` so the first paint is already at the
 * chosen size. Independent of — and composes with — browser zoom and the editor's own zoom.
 *
 * Range: 0.8 (compact) → 1.4 (large). 1.0 is the default. The value updates live as the slider
 * moves; a "Reset" button restores 100%.
 */

import { useState } from 'react';
import { Button } from '@/components/ui';
import { SettingsCard } from './SettingsCard';

/** localStorage key for the persisted scale (1.0 == 100%). */
const STORAGE_KEY = 'aletheia.ui_scale';
const MIN = 0.8;
const MAX = 1.4;
const STEP = 0.05;
const DEFAULT = 1.0;

/** Read the saved scale at boot; safe against missing/garbage values. Exported so the app
 *  shell can call it from `main.tsx` to apply the saved scale before React mounts (so the
 *  first paint is at the user's chosen size, not a flicker at 100%). */
export function loadSavedUiScale(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT;
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n)) return DEFAULT;
    return clamp(n);
  } catch {
    return DEFAULT;
  }
}

/** Apply the scale to the document root via CSS `zoom`, which scales the WHOLE app uniformly
 *  (every px/rem size, all surfaces, plus modals + toasts that portal to <body>). Pure DOM — no
 *  React. Called from the slider's `onChange` AND from `main.tsx` at boot. Uses `setProperty` so
 *  it doesn't depend on `zoom` being on the typed CSSStyleDeclaration. */
export function applyUiScale(scale: number): void {
  document.documentElement.style.setProperty('zoom', String(clamp(scale)));
}

function clamp(n: number): number {
  if (n < MIN) return MIN;
  if (n > MAX) return MAX;
  return Math.round(n * 100) / 100;
}

export function UiScaleSettings(): JSX.Element {
  // `applied` is the live, persisted scale; `draft` is the slider's working value. The slider
  // moves ONLY the draft — applying live would resize the slider out from under the cursor
  // mid-drag — so a deliberate "Apply" commits the draft to the DOM + localStorage.
  const [applied, setApplied] = useState<number>(() => loadSavedUiScale());
  const [draft, setDraft] = useState<number>(applied);

  const commit = (value: number): void => {
    const next = clamp(value);
    applyUiScale(next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // localStorage may be unavailable (private window, quota) — applies in-session, no persist.
    }
    setApplied(next);
    setDraft(next);
  };

  const draftPercent = Math.round(draft * 100);
  const dirty = draft !== applied;

  return (
    <SettingsCard
      id="ui-scale"
      title="UI scale"
      desc="Per-device zoom for the whole app. Slide, then Apply."
    >
      {/* U4: Tailwind-utility mix replaced by settings.css classes.
          U9: paragraph desc dieted to one line; the caveats live here. */}
      <div className="settings-controls">
        <input
          type="range"
          min={MIN}
          max={MAX}
          step={STEP}
          value={draft}
          onChange={(e) => setDraft(Number.parseFloat(e.target.value))}
          aria-label="UI scale"
          className="settings-range"
        />
        <span className="settings-range__value" aria-live="polite">
          {draftPercent}%
        </span>
        <Button variant="default" size="sm" onClick={() => commit(draft)} disabled={!dirty}>
          Apply
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => commit(DEFAULT)}
          disabled={draft === DEFAULT && applied === DEFAULT}
        >
          Reset
        </Button>
      </div>
      <p className="settings-hint">
        Applies on Apply (so the slider doesn't shift mid-drag). Independent of browser zoom.
      </p>
    </SettingsCard>
  );
}
